import type {ComboboxProps} from '@/components/ui/combobox';
import {Combobox} from '@/components/ui/combobox';
import {graphqlClient} from '@/services/graphqlClient';
import {
  ResidentStatus,
  useGetAllCurrentResidentsForInputQuery,
} from '@/services/graphqlHooks';

import type {ResidentOption} from '../utils/residentOptions';
import {renderResidentOption, toResidentOption} from '../utils/residentOptions';

type ResidentOptionsProps = {
  excludeOptionsIds?: string[];
  allowedStatuses?: ResidentStatus[];
};

export type ResidentInputProps = Omit<ComboboxProps<ResidentOption>, 'items'> &
  ResidentOptionsProps & {
    value: string;
    onChange: (value: string) => void;
  };

export type ResidentMultiInputProps = Omit<
  ComboboxProps<ResidentOption>,
  'items' | 'multiple'
> &
  ResidentOptionsProps & {
    value: string[];
    onChange: (value: string[]) => void;
  };

function useResidentOptions({
  excludeOptionsIds = [],
  allowedStatuses = [ResidentStatus.Active, ResidentStatus.Absent],
}: ResidentOptionsProps) {
  const {data} = useGetAllCurrentResidentsForInputQuery(
    graphqlClient,
    undefined,
    {staleTime: Infinity}
  );

  return (data?.getAllCurrentResidents || [])
    .filter(resident => !excludeOptionsIds.includes(resident.id))
    .filter(resident => allowedStatuses.includes(resident.status))
    .map(toResidentOption);
}

const ResidentInput = ({
  value,
  onChange,
  excludeOptionsIds = [],
  allowedStatuses = [ResidentStatus.Active, ResidentStatus.Absent],
  ...props
}: ResidentInputProps) => {
  const residentsOptions = useResidentOptions({
    excludeOptionsIds,
    allowedStatuses,
  });
  const selectedItem =
    residentsOptions.find(option => option.value === value) || null;

  return (
    <Combobox
      items={residentsOptions}
      itemToString={option => option?.label || ''}
      selectedItem={selectedItem}
      onSelectedItemChange={({selectedItem}) => {
        onChange(selectedItem?.value || '');
      }}
      renderItem={renderResidentOption}
      {...props}
    />
  );
};

export const ResidentMultiInput = ({
  value,
  onChange,
  excludeOptionsIds = [],
  allowedStatuses = [ResidentStatus.Active, ResidentStatus.Absent],
  ...props
}: ResidentMultiInputProps) => {
  const residentsOptions = useResidentOptions({
    excludeOptionsIds,
    allowedStatuses,
  });
  const selectedItems = residentsOptions.filter(option =>
    value.includes(option.value)
  );

  return (
    <Combobox
      items={residentsOptions}
      itemToString={option => option?.label || ''}
      itemToKey={option => option?.value}
      multiple={{
        selectedItems,
        onSelectedItemsChange: ({selectedItems}) => {
          onChange(selectedItems?.map(item => item.value) || []);
        },
      }}
      isItemSelected={item => value.includes(item.value)}
      isItemDisabled={item => value.includes(item.value)}
      renderItem={renderResidentOption}
      {...props}
    />
  );
};

export default ResidentInput;
